/**
 * Categories Page
 * ===============
 * Manage product categories with hierarchical nesting support.
 */

import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Edit2, Trash2, FolderTree, ChevronRight, ChevronDown, AlertCircle, X, Package } from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/Card'
import Button from '@/components/Button'
import Input from '@/components/Input'
import { useCategories, useCreateCategory, useDeleteCategory, useUpdateCategory } from '@/hooks/useCategories'
import type { Category, CategoryCreate, CategoryTree } from '@/types'

/**
 * Recursive component to render category with its children.
 */
function CategoryItem({
  category,
  depth,
  onEdit,
  onDelete,
  onAddChild,
  onViewProducts,
  expandedIds,
  toggleExpand,
}: {
  category: CategoryTree
  depth: number
  onEdit: (category: Category) => void
  onDelete: (id: number) => void
  onAddChild: (parentId: number) => void
  onViewProducts: (categoryId: number) => void
  expandedIds: Set<number>
  toggleExpand: (id: number) => void
}) {
  const hasChildren = category.children && category.children.length > 0
  const isExpanded = expandedIds.has(category.id)

  return (
    <>
      <div
        className="px-6 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b dark:border-gray-700 last:border-b-0"
        style={{ paddingLeft: `${24 + depth * 24}px` }}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {hasChildren ? (
            <button
              onClick={() => toggleExpand(category.id)}
              className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
            >
              {isExpanded ? (
                <ChevronDown size={16} className="text-gray-500 dark:text-gray-400" />
              ) : (
                <ChevronRight size={16} className="text-gray-500 dark:text-gray-400" />
              )}
            </button>
          ) : (
            <span className="w-5" />
          )}
          <div className="min-w-0 flex-1">
            <button
              onClick={() => onViewProducts(category.id)}
              className="text-left font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 truncate block"
            >
              {category.name}
            </button>
            {category.description && (
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{category.description}</p>
            )}
          </div>
          {/* Product count badge */}
          <div
            className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
            onClick={() => onViewProducts(category.id)}
            title="View products in this category"
          >
            <Package size={12} />
            <span>{category.product_count}</span>
          </div>
        </div>
        <div className="flex gap-1 ml-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onAddChild(category.id)}
            title="Add subcategory"
          >
            <Plus size={14} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(category)}
            title="Edit"
          >
            <Edit2 size={14} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(category.id)}
            title="Delete"
          >
            <Trash2 size={14} className="text-red-500" />
          </Button>
        </div>
      </div>
      {hasChildren && isExpanded && (
        <>
          {category.children.map((child) => (
            <CategoryItem
              key={child.id}
              category={child}
              depth={depth + 1}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddChild={onAddChild}
              onViewProducts={onViewProducts}
              expandedIds={expandedIds}
              toggleExpand={toggleExpand}
            />
          ))}
        </>
      )}
    </>
  )
}

export default function Categories() {
  const navigate = useNavigate()

  // Fetch hierarchical categories for tree display
  const { data: treeData, isLoading: treeLoading, error: treeError } = useCategories(true)
  // Fetch flat list for parent selector dropdown
  const { data: flatData } = useCategories(false)

  const createCategory = useCreateCategory()
  const updateCategory = useUpdateCategory()
  const deleteCategory = useDeleteCategory()

  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [formData, setFormData] = useState<CategoryCreate>({ name: '', parent_id: null })
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const flatCategories = (flatData?.data ?? []) as Category[]

  // Memoize categoryTree to stabilize reference
  const categoryTree = useMemo(() => (treeData?.data ?? []) as CategoryTree[], [treeData?.data])

  // Collect all category IDs that have children (for auto-expand)
  const allCategoryIdsWithChildren = useMemo(() => {
    const ids: number[] = []
    const collectIds = (categories: CategoryTree[]) => {
      for (const cat of categories) {
        if (cat.children && cat.children.length > 0) {
          ids.push(cat.id)
          collectIds(cat.children)
        }
      }
    }
    collectIds(categoryTree)
    return ids
  }, [categoryTree])

  // Auto-expand all categories when data loads
  useEffect(() => {
    if (allCategoryIdsWithChildren.length > 0 && expandedIds.size === 0) {
      setExpandedIds(new Set(allCategoryIdsWithChildren))
    }
  }, [allCategoryIdsWithChildren, expandedIds.size])

  const handleViewProducts = (categoryId: number) => {
    navigate(`/categories/${categoryId}/products`)
  }

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) return

    if (editingId) {
      await updateCategory.mutateAsync({ id: editingId, data: formData })
      setEditingId(null)
    } else {
      await createCategory.mutateAsync(formData)
      setIsAdding(false)
    }
    setFormData({ name: '', parent_id: null })
  }

  const handleEdit = (category: Category) => {
    setEditingId(category.id)
    setFormData({
      name: category.name,
      description: category.description,
      parent_id: category.parent_id,
    })
    setIsAdding(false)
  }

  const handleAddChild = (parentId: number) => {
    setIsAdding(true)
    setEditingId(null)
    setFormData({ name: '', parent_id: parentId })
    // Expand the parent so user sees the new child
    setExpandedIds((prev) => new Set(prev).add(parentId))
  }

  const handleDelete = async (id: number) => {
    if (confirm('Are you sure you want to delete this category?')) {
      setDeleteError(null)
      try {
        await deleteCategory.mutateAsync({ id })
      } catch (error: unknown) {
        const err = error as { response?: { data?: { detail?: string } } }
        const message = err.response?.data?.detail || 'Failed to delete category'
        setDeleteError(message)
      }
    }
  }

  const handleCancel = () => {
    setIsAdding(false)
    setEditingId(null)
    setFormData({ name: '', parent_id: null })
  }

  // Get parent name for display
  const getParentName = (parentId: number | null | undefined): string => {
    if (!parentId) return ''
    const parent = flatCategories.find((c) => c.id === parentId)
    return parent ? parent.name : ''
  }

  if (treeError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">Error loading categories</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Categories</h1>
          <p className="text-gray-500 dark:text-gray-400">Organize products with hierarchical categories</p>
        </div>
        <Button icon={<Plus size={16} />} onClick={() => { setIsAdding(true); setFormData({ name: '', parent_id: null }); }}>
          Add Category
        </Button>
      </div>

      {/* Delete error message */}
      {deleteError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="text-red-800 dark:text-red-200 font-medium">Cannot delete category</p>
            <p className="text-red-600 dark:text-red-300 text-sm mt-1">{deleteError}</p>
          </div>
          <button
            onClick={() => setDeleteError(null)}
            className="text-red-500 hover:text-red-700 dark:hover:text-red-300"
          >
            <X size={20} />
          </button>
        </div>
      )}

      {/* Add/Edit form */}
      {(isAdding || editingId) && (
        <Card>
          <CardHeader title={editingId ? 'Edit Category' : 'New Category'} />
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Category Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="e.g., Computer Parts"
                required
              />
              <Input
                label="Description (optional)"
                value={formData.description ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value || null,
                  }))
                }
                placeholder="Brief description of this category"
              />
              {/* Parent Category Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Parent Category (optional)
                </label>
                <select
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                  value={formData.parent_id ?? ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      parent_id: e.target.value ? parseInt(e.target.value, 10) : null,
                    }))
                  }
                >
                  <option value="">None (root category)</option>
                  {flatCategories
                    .filter((c) => c.id !== editingId) // Can't be parent of itself
                    .map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                        {cat.parent_id ? ` (in ${getParentName(cat.parent_id)})` : ''}
                      </option>
                    ))}
                </select>
                {formData.parent_id && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    This will be a subcategory of "{getParentName(formData.parent_id)}"
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  loading={createCategory.isPending || updateCategory.isPending}
                >
                  {editingId ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Categories tree */}
      <Card>
        <CardHeader title="All Categories" />
        <div>
          {treeLoading ? (
            <div className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">Loading...</div>
          ) : categoryTree.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <FolderTree className="mx-auto text-gray-300 dark:text-gray-600 mb-4" size={48} />
              <p className="text-gray-500 dark:text-gray-400">No categories yet</p>
              <button
                className="text-primary-600 dark:text-primary-400 hover:underline mt-2"
                onClick={() => setIsAdding(true)}
              >
                Create your first category
              </button>
            </div>
          ) : (
            categoryTree.map((category) => (
              <CategoryItem
                key={category.id}
                category={category}
                depth={0}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onAddChild={handleAddChild}
                onViewProducts={handleViewProducts}
                expandedIds={expandedIds}
                toggleExpand={toggleExpand}
              />
            ))
          )}
        </div>
      </Card>
    </div>
  )
}
